import React from "react";

import { trackEvent } from "../../analytics/analytics";
import { Button } from "../../components/ui/button";
import { useGetLatestAccountId } from "../hooks/useGetLatestAccountId";

import { ErrorState } from "./shared/ErrorState";
import { LoadingState } from "./shared/LoadingState";
import { StatusCard } from "./shared/StatusCard";
import { StepHeader } from "./shared/StepHeader";
import { StepLayout } from "./shared/StepLayout";
import { StepIndicator } from "./StepIndicator";

import type { ModalStepProps } from "../types/modal";

const CATEGORY_SYNC_LABEL: Record<string, string> = {
  emails: "email",
  drives: "file",
  chats: "message",
  notes: "note",
  socialmedia: "data",
};

interface ResultStepProps extends ModalStepProps {
  success: boolean;
  errorMessage?: string | null;
  fromCrawlerPreference?: boolean;
}

export function ResultStep({
  integration,
  integrationScope,
  onClose,
  onConnect,
  success,
  errorMessage,
  fromCrawlerPreference = false,
  selectedAccountId,
}: ResultStepProps) {
  const { accountIdToUse, isFetchingAccounts } = useGetLatestAccountId({
    sourceId: integration.label,
    selectedAccountId,
    enabled: success,
    integrationScope,
  });

  React.useEffect(() => {
    if (success) {
      trackEvent("app_connected", {
        source: integration.label,
        success: "success",
        type: "private",
      });
    }
  }, [success, integration.label]);

  const renderSuccessContent = () => (
    <StatusCard status="success">
      {isFetchingAccounts ? (
        <LoadingState message="Loading connection details..." iconSize="sm" />
      ) : (
        <>
          <p className="text-sm text-zinc-900 dark:text-white">
            Your account <span className="font-semibold">{accountIdToUse}</span>{" "}
            is now connected.
          </p>
          {fromCrawlerPreference ? (
            <div className="text-sm text-zinc-900 dark:text-white">
              <p className="font-semibold">Preferences Updated!</p>
              <p>
                It may take some time for the app to{" "}
                <span className="font-semibold">sync</span> the changes.
              </p>
            </div>
          ) : (
            <div className="text-sm text-zinc-600 dark:text-zinc-300 leading-6">
              <p>
                Your app integration will be active in{" "}
                <span className="font-semibold">1 hour</span> with full{" "}
                {CATEGORY_SYNC_LABEL[integration.category] ?? "data"} syncing
                possibly taking a few days for large volumes. Once active,
                syncing will occur every{" "}
                <span className="font-semibold">2 hours.</span>
              </p>
            </div>
          )}
        </>
      )}
    </StatusCard>
  );

  const renderErrorContent = () => (
    <ErrorState
      title="Connection Failed"
      message={
        errorMessage ||
        `We were unable to connect your ${integration.value} account. This could be due to authentication issues or temporary service unavailability.`
      }
    >
      {!errorMessage && (
        <p className="text-sm text-red-700">
          If the problem persists, please contact our support team at{" "}
          <a
            href="mailto:support@needl.ai"
            className="font-medium underline hover:no-underline"
          >
            support@needl.ai
          </a>
        </p>
      )}
    </ErrorState>
  );

  return (
    <StepLayout
      header={
        <>
          <StepHeader integration={integration} />
          <StepIndicator
            currentStep="result"
            sourceId={integration.label}
            integrationScope={integrationScope}
            success={success}
          />
        </>
      }
      bodyClassName="pt-6"
      footer={
        success ? (
          <Button className="w-full h-10" onClick={onClose}>
            Done
          </Button>
        ) : (
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-10"
              onClick={() => onConnect?.()}
            >
              Try Again
            </Button>
            <Button className="flex-1 h-10" onClick={onClose}>
              Close
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {success ? renderSuccessContent() : renderErrorContent()}
      </div>
    </StepLayout>
  );
}
